import { gridViewManager } from '@/utils/grid'
import './operationsRequests.css'

import { CarryOutOutlined, UserOutlined } from '@ant-design/icons'
import { useOperationsRequests } from '@api/useOperationsRequests'

import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { ActionColumnCellRenderer } from '@components/OrderDispatch/OperationsRequests/ActionColumnCellRenderer'
import { ControlBarActionButtons } from '@components/OrderDispatch/OperationsRequests/ControlBarActionButtons'
import { RequestDrawer } from '@components/OrderDispatch/OperationsRequests/RequestDrawer'
import { UpdateStatusDrawer } from '@components/OrderDispatch/OrderGrid/OrderUpdatedTag'
import { BBDTag, GraviButton, GraviGrid, Texto } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Avatar, Col, Popover, Row } from 'antd'
import moment from 'moment/moment'
import React, { useCallback, useMemo, useRef, useState } from 'react'

const storageKey = 'operations-requests'

export function OperationsRequests() {
  const { value: currentGridConfig, setValue: updateGridConfig } = useLocalStorage(`gridConfig::${storageKey}`, {})
  const [visible, setVisible] = useState(false)
  const [requestItem, setRequestItem] = useState(null)
  const columnApi = useRef(null)
  const gridAPIRef = useRef(null)

  const statusFilter = useMemo(
    () => currentGridConfig?.filter?.status?.values?.[0] || 'All',
    [currentGridConfig?.filter?.status]
  )

  const { updateSetting } = useUserSettings()
  const { value: operationsRequestDayRange, setValue: setORDayRange } = useLocalStorage(
    'operations-request-day-range',
    { back: -15, ahead: 1 }
  )
  const setOperationsRequestDayRange = (value) => {
    setORDayRange(value)
    updateSetting('operations-request-day-range', value)
  }

  const [filters, setFilters] = useState({
    from: moment()
      .add(operationsRequestDayRange.back ? operationsRequestDayRange.back : -15, 'day')
      .startOf('day'),
    to: moment()
      .add(operationsRequestDayRange.ahead ? operationsRequestDayRange.ahead : 1, 'day')
      .endOf('day'),
  })

  const { scopes } = useSecurityContext()
  const editRequestScope = scopes?.scopes.includes('or.w')
  const { getOperationsRequestsQuery } = useOperationsRequests()
  const { data, loading, fetching } = getOperationsRequestsQuery({
    from_date: moment(filters.from).toISOString(true),
    to_date: moment(filters.to).toISOString(true),
  })
  const statusOptions = useMemo(
    () => [
      {
        value: 'All',
        label: 'All',
      },
      {
        value: 'Open',
        label: 'Open',
      },
      {
        value: 'Complete',
        label: 'Complete',
      },
    ],
    []
  )

  const columnDefs = useMemo(() => getColumnDefs(editRequestScope, setVisible, setRequestItem), [])

  const getRowId = useCallback((row) => row.data._id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
    }),
    [getRowId]
  )

  const handleSetStatus = (value) => {
    // grab snapshot of current filter model so we don't lose other filters
    const currentFilterModel = gridAPIRef?.current?.getFilterModel()

    const filterChange = {
      ...currentFilterModel,
      status: value === 'All' ? null : { filterType: 'set', values: [value] },
    }

    // take existing filters, but remove status filter, then get another snapshot
    gridAPIRef?.current?.setFilterModel(filterChange)
    const updatedFilterModel = { ...gridAPIRef?.current?.getFilterModel() }

    // Keeping local grid config in sync with the new change
    const updatedGridConfig = {
      ...currentGridConfig,
      filter: { ...currentGridConfig.filter, ...updatedFilterModel },
    }

    // 'All' status means no filter, so we'll need to remove status from the current config if that's what was selected
    if (!Object.hasOwn(updatedFilterModel, 'status')) {
      delete updatedGridConfig.filter.status
    }

    updateGridConfig(updatedGridConfig)
    updateSetting(`gridConfig::${storageKey}`, updatedGridConfig)
  }

  return (
    <>
      <GraviGrid
        columnApiRef={columnApi}
        getRowStyle={(params) => {
          const diff = moment().diff(params.data?.updated_date || params.data?.created_date, 'seconds')
          if (diff < 3) {
            return { backgroundColor: 'var(--theme-color-2-dim)' }
          }
        }}
        controlBarProps={{
          title: 'Operations Requests',
          actionButtons: (
            <ControlBarActionButtons
              setVisible={setVisible}
              editScope={editRequestScope}
              filters={filters}
              setFilters={setFilters}
              selectedDayRange={operationsRequestDayRange}
              setSelectedDayRange={setOperationsRequestDayRange}
              statusOptions={statusOptions}
              selectedStatus={statusFilter}
              setSelectedStatus={handleSetStatus}
            />
          ),
        }}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        loading={loading || fetching}
        rowData={data?.rows}
        externalRef={gridAPIRef}
        storageKey={storageKey}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${storageKey}`, config)}
        {...gridViewManager(storageKey, updateSetting)}
      />
      <RequestDrawer
        visible={visible}
        setVisible={setVisible}
        requestItem={requestItem}
        setRequestItem={setRequestItem}
        sites={data?.sites}
        operationTypes={data?.operation_types}
        ownerOptions={data?.owners}
        editRequestScope={editRequestScope}
      />
    </>
  )
}

const getColumnDefs = (editRequestScope, setVisible, setRequestItem) => [
  {
    headerName: '',
    field: 'id',
    key: 'view',
    maxWidth: 60,
    cellRenderer: (params) => {
      const title = !editRequestScope || params?.data?.status === 'complete' ? 'View' : 'View or Edit'
      const itemToSet = {
        ...params.data,
        from_date: moment(params.data.from_date),
        to_date: moment(params.data.to_date),
      }
      return (
        <GraviButton
          title={title}
          icon={<CarryOutOutlined />}
          onClick={() => {
            setVisible(true)
            setRequestItem(itemToSet)
          }}
        />
      )
    },
  },
  {
    headerName: 'Status',
    field: 'status',
    cellRenderer: (params) => {
      const val = params?.data.status
      if (val) {
        const cap = val.charAt(0)?.toUpperCase() + val.slice(1)
        if (val === 'open') return <BBDTag theme2>{cap}</BBDTag>
        if (val === 'complete') return <BBDTag success> {cap} </BBDTag>
      }
    },
    valueGetter: (params) => params.data.status?.charAt(0)?.toUpperCase() + params.data.status?.slice(1),
  },
  {
    headerName: 'Request Number',
    field: 'number',
  },
  {
    headerName: 'Title',
    field: 'subject',
    cellRenderer: ({ value }) => {
      return <Texto className='text-truncate'>{value}</Texto>
    },
  },
  {
    headerName: 'Site',
    field: 'site',
    comparator: (a, b) => b - a,
  },
  {
    headerName: 'Type',
    field: 'type',
  },
  {
    headerName: 'From',
    field: 'from_date',
    ...defaultDateDef('from_date', formatters.DATE, true),
  },
  {
    headerName: 'To',
    field: 'to_date',
    ...defaultDateDef('to_date', formatters.DATE, true),
  },
  {
    headerName: 'Owner',
    field: 'owner',
    cellRenderer: ({ value }) => {
      if (value) return <Texto className='text-truncate'>{value}</Texto>
      return <BBDTag warning> Needs Assignment </BBDTag>
    },
    minWidth: 155,
  },
  {
    headerName: 'Updated Date',
    field: 'updated_date',
    ...defaultDateDef('updated_date', formatters.DATE_TIME, true),
    minWidth: 140,
    cellRenderer: (params) => {
      const content = {
        ...params?.data,
      }
      return (
        <Popover
          title='Request History'
          content={<UpdateStatusDrawer order={content} ignoreCreatedIfNull />}
          trigger='hover'
        >
          <Row className='order-stat-tag-container' style={{ flexWrap: 'nowrap' }}>
            <Col className='vertical-flex-center'>
              <Avatar
                style={{
                  width: 24,
                  height: 24,
                  backgroundColor: 'var(--theme-color-2-dim)',
                  color: 'var(--theme-color-1)',
                  border: '1px solid var(--gray-300)',
                }}
                icon={<UserOutlined />}
              />
            </Col>
            <Col flex={1} className=' pl-3 pr-4  vertical-flex-center h-100'>
              <div style={{ fontWeight: params?.data?.updated_date ? 'bold' : 'normal' }}>
                {formatters.SHORT_DATE_TIME(
                  moment(params?.data?.updated_date ? params.data.updated_date : params?.data?.created_date)
                )}
              </div>
            </Col>
          </Row>
        </Popover>
      )
    },
    valueGetter: (params) => formatters.SHORT_DATE_TIME(params.data.updated_date || params.data.created_date),
  },
  {
    headerName: 'Updated By',
    field: 'updated_by',
    cellRenderer: ({ value }) => value && <Texto className='text-truncate'>{value}</Texto>,
  },
  {
    headerName: 'Actions',
    field: 'status',
    key: 'actions',
    sortable: false,
    filter: false,
    cellRenderer: (params) => {
      if (editRequestScope) return ActionColumnCellRenderer(params?.data?.status, params)
      return null
    },
    hide: !editRequestScope,
  },
]
